import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Inventory,
  PointOfSale,
  Receipt,
  Settings,
  AccountCircle,
  Logout,
  Brightness4,
  Brightness7,
  Email,
  Assessment,
  ShoppingCart,
  ShoppingBag,
  Person,
  Language
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBusiness } from '../../context/BusinessContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import contactService from '../../services/contactService';
import orderService from '../../services/orderService';
import './MainLayout.css';

const drawerWidth = 260;

const MainLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isCliente } = useAuth();
  const { businessInfo } = useBusiness();
  const { mode, toggleTheme } = useCustomTheme();

  const loadUnreadMessagesCount = async () => {
    if (user?.role !== 'admin') return;

    const result = await contactService.getStats();
    if (result.success) {
      setUnreadMessagesCount(result.data?.por_estado?.nuevos || 0);
    }
  };

  const loadPendingOrdersCount = async () => {
    if (!['admin', 'vendedor'].includes(user?.role)) return;
    const result = await orderService.getStats();
    if (result.success) {
      setPendingOrdersCount(result.data?.pendiente || 0);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') return;

    loadUnreadMessagesCount();
    const interval = setInterval(loadUnreadMessagesCount, 60000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    loadUnreadMessagesCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!['admin', 'vendedor'].includes(user?.role)) return;
    loadPendingOrdersCount();
    const interval = setInterval(loadPendingOrdersCount, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    if (!['admin', 'vendedor'].includes(user?.role)) return;
    loadPendingOrdersCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    document.title = pendingOrdersCount > 0
      ? `(${pendingOrdersCount}) Pedidos nuevos · Descorcha2`
      : 'Descorcha2 · Vinoteca';
    return () => {
      document.title = 'Descorcha2 · Vinoteca';
    };
  }, [pendingOrdersCount]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  // Menú diferente para clientes
  const menuItemsCliente = [
    { text: 'Nuestra Cava', icon: <ShoppingCart />, path: '/catalog-client', roles: ['cliente'] },
    { text: 'Mis Pedidos', icon: <ShoppingBag />, path: '/my-orders', roles: ['cliente'] },
    { text: 'Mi Perfil', icon: <Person />, path: '/profile', roles: ['cliente'] }
  ];

  // Menú para admin y vendedores
  const menuItemsAdminVendedor = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', roles: ['admin', 'vendedor'] },
    { text: 'Punto de Venta', icon: <PointOfSale />, path: '/pos', roles: ['admin', 'vendedor'] },
    { text: 'Productos', icon: <Inventory />, path: '/products', roles: ['admin'] },
    { text: 'Ventas', icon: <Receipt />, path: '/sales', roles: ['admin', 'vendedor'] },
    { text: 'Pedidos', icon: <ShoppingBag />, path: '/orders', roles: ['admin', 'vendedor'] },
    { text: 'Clientes', icon: <Person />, path: '/customers', roles: ['admin', 'vendedor'] },
    { text: 'Reportes', icon: <Assessment />, path: '/reports', roles: ['admin'] },
    { text: 'Facturas', icon: <Receipt />, path: '/invoices', roles: ['admin', 'vendedor'] },
    { text: 'Mensajes', icon: <Email />, path: '/contact-messages', roles: ['admin'] },
    { text: 'Configuración', icon: <Settings />, path: '/settings', roles: ['admin'] }
  ];

  // Seleccionar menú según rol
  const menuItems = isCliente ? menuItemsCliente : menuItemsAdminVendedor;

  const drawer = (
    <div className="admin-sidebar-content">
      <Toolbar className="admin-sidebar-brand">
        <Box component="img" src="/descorcha2-logo.png" alt="Descorcha2" />
        <Box className="admin-sidebar-brand-copy">
          <Typography component="strong">
            {businessInfo?.businessName || 'Descorcha2'}
          </Typography>
          <Typography component="span">Vinoteca · POS</Typography>
        </Box>
      </Toolbar>
      <Box className="admin-sidebar-section-label">GESTIÓN</Box>
      <List className="admin-sidebar-list">
        {menuItems
          .filter(item => {
            if (!item.roles || item.roles.length === 0) return true;
            return item.roles.includes(user?.role);
          })
          .map((item) => {
            const isMessagesItem = item.path === '/contact-messages';
            const showUnreadBadge = isMessagesItem && unreadMessagesCount > 0;
            const isOrdersItem = item.path === '/orders';
            const showOrdersBadge = isOrdersItem && pendingOrdersCount > 0;

            return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                className="admin-sidebar-item"
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    handleDrawerToggle();
                  }
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box component="span">{item.text}</Box>
                      {showUnreadBadge && (
                        <Chip
                          label={unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                          color="error"
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 20,
                            fontSize: '0.75rem',
                            fontWeight: 700
                          }}
                        />
                      )}
                      {showOrdersBadge && (
                        <Chip
                          label={pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}
                          size="small"
                          className="admin-order-badge"
                        />
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          )})}
      </List>
      <Box className="admin-sidebar-site-link">
        <Divider />
        <ListItemButton onClick={() => {
          navigate('/sitio');
          if (isMobile) handleDrawerToggle();
        }}>
          <ListItemIcon><Language /></ListItemIcon>
          <ListItemText primary="Volver al sitio" secondary="Ver página pública" />
        </ListItemButton>
      </Box>
    </div>
  );

  return (
    <Box className="d-flex">
      <CssBaseline />
      <AppBar
        position="fixed"
        className="admin-topbar"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` }
        }}
      >
        <Toolbar className="admin-topbar-toolbar">
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            aria-controls="admin-mobile-menu"
            aria-expanded={mobileOpen}
            sx={{ mr: { xs: 1, sm: 2 }, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box className="admin-topbar-title flex-1">
            <Typography component="strong">Panel de gestión</Typography>
            <Typography component="span">Descorcha2 · Wines & Drinks</Typography>
          </Box>
          <Box className="d-flex align-center gap-sm">
            <IconButton onClick={toggleTheme} color="inherit" title="Cambiar tema" className="admin-topbar-action">
              {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
            <Typography variant="body2" className="hide-sm">
              {user?.fullName}
            </Typography>
            <IconButton onClick={handleMenuOpen} color="inherit" className="admin-user-button">
              <Avatar sx={{ width: 34, height: 34 }}>
                {user?.fullName?.charAt(0)}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { navigate('/sitio'); handleMenuClose(); }}>
          <ListItemIcon>
            <Language fontSize="small" />
          </ListItemIcon>
          Volver al sitio
        </MenuItem>
        <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Mi Perfil
        </MenuItem>
        <MenuItem onClick={toggleTheme}>
          <ListItemIcon>
            {mode === 'dark' ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
          </ListItemIcon>
          {mode === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Cerrar Sesión
        </MenuItem>
      </Menu>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: `min(88vw, ${drawerWidth}px)`,
              height: '100dvh',
              overflow: 'hidden'
            }
          }}
          PaperProps={{ id: 'admin-mobile-menu' }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        className="flex-1 admin-main"
        sx={{
          p: { xs: 2, sm: 3, lg: 4 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;
